/**
 * Paste or drop images into the editor: upload them to the project's figures
 * folder and insert a figure (or a bare \includegraphics inside an existing one).
 */
import { Facet, StateEffect, StateField, type EditorState, type Extension } from "@codemirror/state";
import { Decoration, EditorView, WidgetType, type DecorationSet } from "@codemirror/view";

export interface ImageOptions {
  canEdit: () => boolean;
  /** Folder for new images, e.g. "figures". */
  folder: () => string;
  /** Upload under a free name near `path`; resolves to the path actually used. */
  upload: (path: string, data: Blob) => Promise<string>;
  onError: (message: string) => void;
}

/** Formats pdfLaTeX, XeLaTeX and LuaLaTeX include directly. */
const DIRECT = /\.(png|jpe?g|pdf|eps)$/i;
const DIRECT_TYPES = new Set(["image/png", "image/jpeg", "application/pdf", "application/postscript"]);
/** Folders people usually keep figures in, in order of preference. */
const FIGURE_DIRS = ["figures", "figs", "fig", "images", "img", "graphics", "pictures"];

function isImage(file: File): boolean {
  return file.type.startsWith("image/") || DIRECT.test(file.name) || DIRECT_TYPES.has(file.type);
}

/** The figures folder an existing project already uses, or "figures". */
export function figureFolder(files: string[]): string {
  const imageDirs = new Map<string, string>();
  for (const f of files) {
    const slash = f.indexOf("/");
    if (slash > 0 && /\.(png|jpe?g|pdf|eps|svg)$/i.test(f)) imageDirs.set(f.slice(0, slash).toLowerCase(), f.slice(0, slash));
  }
  for (const dir of FIGURE_DIRS) if (imageDirs.has(dir)) return imageDirs.get(dir)!;
  return "figures";
}

/** A file name LaTeX is happy with: no spaces, a single dot before the extension. */
export function safeImageName(name: string, ext: string): string {
  const base = name.replace(/\.[^.]*$/, "");
  const stem = base
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${stem || "image"}.${ext}`;
}

function pastedName(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `pasted-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

/** Re-encode formats TeX can't read (GIF, WebP, SVG, ...) as PNG. */
async function toPng(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    // SVGs without a size report 0; render those at a sensible width.
    const scale = file.type === "image/svg+xml" ? 2 : 1;
    const width = (img.naturalWidth || 600) * scale;
    const height = (img.naturalHeight || Math.round((width * 3) / 4)) * (img.naturalHeight ? scale : 1);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
    return await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't convert the image."))), "image/png"));
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function prepare(file: File, pasted: boolean): Promise<{ name: string; data: Blob }> {
  const generic = pasted && /^image\.\w+$/i.test(file.name);
  const raw = generic || !file.name ? pastedName() : file.name;
  if (DIRECT.test(file.name) || DIRECT_TYPES.has(file.type)) {
    const ext = /\.([a-z]+)$/i.exec(file.name)?.[1]?.toLowerCase() ?? (file.type === "image/jpeg" ? "jpg" : file.type === "application/pdf" ? "pdf" : "png");
    return { name: safeImageName(raw, ext === "jpeg" ? "jpg" : ext), data: file };
  }
  return { name: safeImageName(raw, "png"), data: await toPng(file) };
}

/** True when `pos` is inside a figure-like environment that's still open. */
function insideFigure(state: EditorState, pos: number): boolean {
  const before = state.sliceDoc(Math.max(0, pos - 20_000), pos);
  const open = before.lastIndexOf("\\begin{figure");
  const sub = before.lastIndexOf("\\begin{subfigure");
  const begin = Math.max(open, sub, before.lastIndexOf("\\begin{wrapfigure"));
  const end = Math.max(before.lastIndexOf("\\end{figure"), before.lastIndexOf("\\end{wrapfigure"));
  return begin > end;
}

/** What to insert for an image at `pos`, and where the cursor should go within it. */
export function imageSnippet(state: EditorState, pos: number, path: string): { text: string; cursor: number } {
  const line = state.doc.lineAt(pos);
  const indent = /^\s*/.exec(line.text)![0];
  const blankLine = line.text.trim() === "";
  if (!blankLine || insideFigure(state, pos)) {
    const include = `\\includegraphics[width=\\linewidth]{${path}}`;
    const before = line.text.slice(0, pos - line.from);
    const after = line.text.slice(pos - line.from);
    // Keep \includegraphics on a line of its own rather than gluing it to the command next to it.
    if (before.trim() && !after.trim()) return { text: `\n${indent}${include}`, cursor: indent.length + 1 + include.length };
    if (!before.trim() && after.trim()) return { text: `${include}\n${before}`, cursor: include.length };
    return { text: include, cursor: include.length };
  }
  const label = path.replace(/^.*\//, "").replace(/\.[^.]*$/, "");
  const head = `\\begin{figure}[htbp]\n${indent}  \\centering\n${indent}  \\includegraphics[width=0.8\\linewidth]{${path}}\n${indent}  \\caption{`;
  const text = `${head}}\n${indent}  \\label{fig:${label}}\n${indent}\\end{figure}`;
  return { text, cursor: head.length };
}

// ---------------------------------------------------------------- pending uploads

const addPending = StateEffect.define<{ id: number; pos: number; label: string }>();
const movePending = StateEffect.define<{ id: number; pos: number }>();
const removePending = StateEffect.define<number>();

class UploadWidget extends WidgetType {
  constructor(readonly label: string) {
    super();
  }
  eq(other: UploadWidget) {
    return other.label === this.label;
  }
  toDOM() {
    const span = document.createElement("span");
    span.className = "cm-upload-pending";
    span.textContent = this.label;
    return span;
  }
}

/** Where pending uploads will land, kept in step with everyone's edits. */
const pending = StateField.define<Array<{ id: number; pos: number; label: string }>>({
  create: () => [],
  update(value, tr) {
    let next = tr.docChanged ? value.map((p) => ({ ...p, pos: tr.changes.mapPos(p.pos, 1) })) : value;
    for (const e of tr.effects) {
      if (e.is(addPending)) next = [...next, e.value];
      else if (e.is(movePending)) next = next.map((p) => (p.id === e.value.id ? { ...p, pos: e.value.pos } : p));
      else if (e.is(removePending)) next = next.filter((p) => p.id !== e.value);
    }
    return next;
  },
  provide: (f) =>
    EditorView.decorations.from(f, (list): DecorationSet =>
      Decoration.set(
        list.map((p) => Decoration.widget({ widget: new UploadWidget(p.label), side: 1 }).range(p.pos)),
        true,
      ),
    ),
});

let nextId = 1;

async function insertImages(view: EditorView, files: File[], pos: number, pasted: boolean, opts: ImageOptions) {
  const id = nextId++;
  const startHead = view.state.selection.main.head;
  view.dispatch({ effects: addPending.of({ id, pos, label: files.length > 1 ? `Uploading ${files.length} images…` : "Uploading image…" }) });
  const where = () => view.state.field(pending).find((p) => p.id === id)?.pos ?? Math.min(pos, view.state.doc.length);
  // Only move the cursor if it hasn't gone anywhere else while the upload ran.
  let followCursor = view.state.selection.main.empty && startHead === pos;
  try {
    for (let i = 0; i < files.length; i++) {
      const { name, data } = await prepare(files[i], pasted);
      const path = await opts.upload(`${opts.folder()}/${name}`, data);
      if (!view.dom.isConnected) return; // the file was closed; the image is still in the project
      const at = where();
      const line = view.state.doc.lineAt(at);
      const indent = /^\s*/.exec(line.text)![0];
      // On a blank line, start after its indentation so the figure lines up with it.
      const insertAt = line.text.trim() === "" ? Math.max(at, line.from + indent.length) : at;
      const { text, cursor } = imageSnippet(view.state, insertAt, path);
      const last = i === files.length - 1;
      const separator = last ? "" : text.startsWith("\\begin") ? `\n\n${indent}` : `\n${indent}`;
      followCursor = followCursor && view.state.selection.main.empty && view.state.selection.main.head === at;
      view.dispatch({
        changes: { from: insertAt, insert: text + separator },
        selection: followCursor ? { anchor: last ? insertAt + cursor : insertAt + text.length + separator.length } : undefined,
        effects: movePending.of({ id, pos: insertAt + text.length + separator.length }),
        scrollIntoView: followCursor && last,
        userEvent: pasted ? "input.paste" : "input.drop",
      });
    }
  } catch (e) {
    opts.onError(e instanceof Error ? e.message : String(e));
  } finally {
    if (view.dom.isConnected) view.dispatch({ effects: removePending.of(id) });
  }
}

const imageOptions = Facet.define<ImageOptions, ImageOptions | null>({ combine: (v) => v[v.length - 1] ?? null });

/**
 * Upload images picked some other way (e.g. the toolbar's file picker) and insert them at the
 * cursor, the same as dropping them there. On a line with text, the figure goes on a new line below.
 */
export function insertImageFiles(view: EditorView, files: File[]): boolean {
  const opts = view.state.facet(imageOptions);
  if (!opts || !opts.canEdit()) return false;
  const images = files.filter(isImage);
  if (images.length < files.length) opts.onError("Only images can be inserted as figures. Use the file tree for other files.");
  if (!images.length) return false;
  let pos = view.state.selection.main.head;
  const line = view.state.doc.lineAt(pos);
  if (line.text.trim() && !insideFigure(view.state, pos)) {
    const indent = /^\s*/.exec(line.text)![0];
    view.dispatch({ changes: { from: line.to, insert: `\n${indent}` }, selection: { anchor: line.to + 1 + indent.length } });
    pos = line.to + 1 + indent.length;
  }
  view.focus();
  insertImages(view, images, pos, false, opts);
  return true;
}

export function imageInsertion(opts: ImageOptions): Extension {
  return [
    imageOptions.of(opts),
    pending,
    EditorView.domEventHandlers({
      paste(event, view) {
        const data = event.clipboardData;
        if (!data || !opts.canEdit()) return false;
        const images = [...data.files].filter(isImage);
        if (!images.length) return false;
        // Office apps put both text and a picture of it on the clipboard: keep the text.
        const text = data.getData("text/plain").trim();
        if (text && !images.some((f) => text.endsWith(f.name))) return false;
        event.preventDefault();
        insertImages(view, images, view.state.selection.main.head, true, opts);
        return true;
      },
      dragover(event) {
        if (!opts.canEdit() || !event.dataTransfer?.types.includes("Files")) return false;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        return false;
      },
      drop(event, view) {
        const files = [...(event.dataTransfer?.files ?? [])];
        if (!files.length || !opts.canEdit()) return false;
        const images = files.filter(isImage);
        event.preventDefault();
        if (images.length < files.length) opts.onError("Only images can be dropped into the editor. Use the file tree for other files.");
        if (!images.length) return true;
        const pos = view.posAtCoords({ x: event.clientX, y: event.clientY }) ?? view.state.selection.main.head;
        view.focus();
        view.dispatch({ selection: { anchor: pos } });
        insertImages(view, images, pos, false, opts);
        return true;
      },
    }),
  ];
}
